import { useEffect, useRef } from 'react';
import {
  Booking,
  BookingStatus,
  enhancedApi,
  ListBookingsV2ApiArg,
  Status,
  useCancelParkingBookingV2Mutation,
} from '../../codegen/bookings';
import { useAppDispatch, useAppSelector } from '../hooks';
import { getSelectedBooking } from '../../pages/bookings/state/bookingsUiSlice';
import { RootState } from '../store';
import { bookingsApiTemplateSlice } from './bookingsApiTemplateSlice';
import { useBookingUpdateState } from './bookingsUpdateSlice';

type CancelResult = { data: Booking } | { error: any };

export const useCancelSelectedBooking = () => {
  const selectedBooking = useAppSelector(getSelectedBooking);

  const { setCanceledBookingId } = useBookingUpdateState();

  const dispatch = useAppDispatch();

  const [cancel, { isLoading, error, status, isSuccess, reset }] =
    useCancelParkingBookingV2Mutation();

  const previousBookingId = useRef(selectedBooking?.id);
  useEffect(() => {
    if (previousBookingId.current !== selectedBooking?.id) {
      previousBookingId.current = selectedBooking?.id;
      reset();
    }
  }, [selectedBooking?.id, reset]);

  const bookingsApiState = useAppSelector(
    (state: RootState) => state[bookingsApiTemplateSlice.reducerPath],
  );

  const cancelParkingBooking = () => {
    if (!selectedBooking) {
      return;
    }

    const bookingId = selectedBooking.id;

    cancel({
      bookingId,
      cancelParkingLotRequest: {
        status: Status.Canceled,
      },
    }).then((result: CancelResult) => {
      if ('error' in result) {
        return;
      }

      setCanceledBookingId(bookingId);

      updateCachedBookings();
    });
  };

  const updateCachedBookings = () => {
    const cachedListBookingsArgs: ListBookingsV2ApiArg[] = Object.values(
      bookingsApiState.queries,
    )
      .filter((query) => query?.endpointName === 'listBookingsV2')
      .map((entry) => entry?.originalArgs) as ListBookingsV2ApiArg[];

    for (const args of cachedListBookingsArgs) {
      dispatch(
        enhancedApi.util.updateQueryData(
          'listBookingsV2',
          args,
          (draftBookings) => {
            draftBookings.items.forEach((booking: Booking) => {
              if (booking.id === selectedBooking?.id) {
                booking.status = BookingStatus.Canceled;
              }
            });
          },
        ),
      );
    }
  };

  return {
    cancelParkingBooking,
    isLoading,
    error,
    status,
    isSuccess,
  };
};
